/**
 * Interface representing a Material Request from the API
 */
export interface MaterialRequest {
  prchOrdrId: number;
  statusCd: string;
  projectId: number;
  projectName: string;
  projectAddressId: number;
  projectAddressLine1: string;
  projectAddressLine2: string;
  projectCity: string;
  projectCountry: string;
  projectState: string;
  projectPincode: string;
  projectLatitude: number;
  projectLongitude: number;
  projectCityLan: number;
  projectCityLong: number;
  siteAccess: string;
  roadAccess: string;
  deliveryDate: string;
  customerId: number;
  deliveryAddressId: number;
  deliveryAddressLine1: string;
  deliveryAddressLine2: string;
  deliveryCity: string;
  deliveryCountry: string;
  deliveryState: string;
  deliveryPincode: string;
  deliveryLatitude: number;
  deliveryLongitude: number;
  deliveryCityLan: number;
  deliveryCityLong: number;
  mvtItemId: number;
  mvtItemName: string;
  quantity: number;
  price: number | null;
  variant1OptionGroupId: number | null;
  variant1OptionGroupName: string | null;
  variant1OptionIds: string | null;
  variant1Options: string | null;
  variant2OptionGroupId: number | null;
  variant2OptionGroupName: string | null;
  variant2OptionIds: string | null;
  variant2Options: string | null;
  variant3OptionGroupId: number | null;
  variant3OptionGroupName: string | null;
  variant3OptionIds: string | null;
  variant3Options: string | null;
  optionGroupId: number | null;
  optionGroupName: string | null;
  optionId: number | null;
  optionName: string | null;
  createdAt: string;
  updatedAt: string;
  prchOrdrSubmittedAt?: string | null;
  vendorId: number;
  cappCategoriesId: number;
  cappCategoriesName: string;
  vendorName: string;
  image1Thumbnail: string | null;
  image1: string | null;
  image2Thumbnail: string | null;
  image2: string | null;
  image3Thumbnail: string | null;
  image3: string | null;
  image4Thumbnail: string | null;
  image4: string | null;
  image5Thumbnail: string | null;
  image5: string | null;
  instructions: string;
  customerName: string;
  customerPhone: string;
  vendorRating: number;
  siteInChargeId: number | null;
  orderGroupId: number;
  orderGroupName: string;
  statusName: string;
  mrReadYn: string;
  sellerCustomerId: number | null;
  sellerVendorId: number | null;
  sellerVendorName: string | null;
  statusCdId: number;
  assignedCustomers: AssignedCustomer[];
  customerRole: string | null;
  mvtItemImage: string;
  createdBy: string;
  isMr: boolean;
  childMrYn: string | null;
  orderGrpNo: number;
  prchOrdrSplitId: number | null;
  negoYN: string | null;
  prchOrdrSplitName: string | null;
  buyerNegoYN: string | null;
  sellerNegoYN: string | null;
  mrStatusButtonColor: string | null;
  isNego: string | null;
  quoteSubmittedYN?: string | null;
  /** Quotation-specific status from the API. Overrides statusCd for display when present. */
  quotationStatus?: string | null;
  noOfProducts: number;
  products?: MaterialRequestProduct[] | null;
  accessRestrictions?: string | null;
  priorityLevel?: string | null;
  receivingContactPersonId?: number | null;
  receivingContactPersonName?: string | null;
  receivingContactPersonNumber?: string | null;
  negotiationContactPersonId?: number | null;
  deliveryTimeSlotId?: number | null;
  prchOrdrDeliveryTimeSlot?: DeliveryTimeSlot | null;
  contactPersonDetails?: ContactPerson | null;
  receivingPerson?: ContactPerson | null;
  deliveryAddress?: DeliveryAddressSummary | null;
  negotiateContactPerson?: ContactPerson| null;
}

export interface DeliveryTimeSlot {
  id?: number | null;
  code?: string | null;
  label?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
}

export interface ContactPerson {
  name?: string | null;
  designation?: string | null;
  phone?: string | null;
  email?: string | null;
}

/**
 * A single product line within a material request.
 * `name` typically already embeds the quantity/unit (e.g. "Cement (Bags)-Blended - 50 MT");
 * `quantity`/`unit` are surfaced separately when the API provides them.
 */
export interface MaterialRequestProduct {
  name?: string | null;
  quantity?: number | null;
  unit?: string | null;
}

export interface DeliveryAddressSummary {
  id?: number | null;
  sellingAddressLine1?: string | null;
  sellingAddressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  lorryDriverName?: string | null;
  lorryNumber?: string | null;
  lorryDriverPhone?: string | null;
}

/**
 * Interface for assigned customers in a material request
 */
export interface AssignedCustomer {
  id: number;
  customer_id: number | null;
  vendor_id: number;
  purchase_order_id: number;
  current_status: string;
  prch_ordr_split_id: number | null;
  delivery_date: string | null;
  inserted_vendor_id: number;
  inserted_customer_id: number | null;
}

/**
 * Interface for API response pagination
 */
export interface Pageable {
  sort: Sort;
  offset: number;
  pageNumber: number;
  pageSize: number;
  paged: boolean;
  unpaged: boolean;
}

/**
 * Interface for sorting information
 */
export interface Sort {
  sorted: boolean;
  unsorted: boolean;
  empty: boolean;
}

/**
 * Interface for the complete API response
 */
export interface MaterialRequestResponse {
  content: MaterialRequest[];
  pageable: Pageable;
  last: boolean;
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  sort: Sort;
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

/**
 * Enum for Material Request Status
 */
export enum MrStatus {
  NEW = 'NEWW',
  QUOTED = 'QUOT',
  SPLIT = 'SPLT',
  EDIT = 'EDIT',
  NEGOTIATION = 'NEGO',
  VENDOR_ASSIGNED = 'VASS',
  TRUSTED = 'TRST',
  ORDER_CONFIRMED = 'ODCF',
  ORDER_DELIVERED_PARTIALLY = 'ODDP',
  GENERATED_PO = 'GNPO',
  CLOSED = 'CLSD',
  DELETED = 'DLTD',
  APPROVED_QUOTE = 'APQV',
  RETURNED_QUOTE = 'RTQV'
}

/**
 * Interface for status display mapping
 */
export interface StatusInfo {
  code: string;
  name: string;
  color: string;
  icon: string;
}

/**
 * Status configuration for UI display
 */
export const STATUS_CONFIG: Record<string, StatusInfo> = {
  'NEW': {
    code: 'NEW',
    name: 'New',
    color: 'blue',
    icon: 'heroicons_outline:plus-circle'
  },
  'REAR': {
    code: 'REAR',
    name: 'Material Rearranging from another site',
    color: 'amber',
    icon: 'heroicons_outline:arrow-path'
  },
  'REJD': {
    code: 'REJD',
    name: 'Request Rejected',
    color: 'red',
    icon: 'heroicons_outline:x-circle'
  },
  'EDIT': {
    code: 'EDIT',
    name: 'Edited',
    color: 'amber',
    icon: 'heroicons_outline:pencil'
  },
  'QUOT': {
    code: 'QUOT',
    name: 'Quotation Stage',
    color: 'orange',
    icon: 'heroicons_outline:document-text'
  },
  'NEGO': {
    code: 'NEGO',
    name: 'Negotiation stage',
    color: 'blue',
    icon: 'heroicons_outline:chat-bubble-left-right'
  },
  'VASS': {
    code: 'VASS',
    name: 'Quote Selected',
    color: 'violet',
    icon: 'heroicons_outline:check-badge'
  },
  'TRST': {
    code: 'TRST',
    name: 'Waiting for seller',
    color: 'yellow',
    icon: 'heroicons_outline:clock'
  },
  'ODCF': {
    code: 'ODCF',
    name: 'Order Confirmed',
    color: 'emerald',
    icon: 'heroicons_outline:check-circle'
  },
  'ACPT': {
    code: 'ACPT',
    name: 'Accepted',
    color: 'emerald',
    icon: 'heroicons_outline:check'
  },
  'ACWC': {
    code: 'ACWC',
    name: 'Accepted with comments',
    color: 'emerald',
    icon: 'heroicons_outline:chat-bubble-left'
  },
  'MREJ': {
    code: 'MREJ',
    name: 'Material Rejected',
    color: 'red',
    icon: 'heroicons_outline:x-mark'
  },
  'APPR': {
    code: 'APPR',
    name: 'Approved',
    color: 'green',
    icon: 'heroicons_outline:check-badge'
  },
  'RJCT': {
    code: 'RJCT',
    name: 'Rejected',
    color: 'red',
    icon: 'heroicons_outline:x-circle'
  },
  'SPLT': {
    code: 'SPLT',
    name: 'Split',
    color: 'purple',
    icon: 'heroicons_outline:scissors'
  },
  'RBYR': {
    code: 'RBYR',
    name: 'Rejected by Buyer',
    color: 'red',
    icon: 'heroicons_outline:user-minus'
  },
  'RSLR': {
    code: 'RSLR',
    name: 'Rejected by Seller',
    color: 'red',
    icon: 'heroicons_outline:user-minus'
  },
  'WFSL': {
    code: 'WFSL',
    name: 'Waiting for Seller',
    color: 'yellow',
    icon: 'heroicons_outline:clock'
  },
  'WFBL': {
    code: 'WFBL',
    name: 'Waiting for Buyer',
    color: 'blue',
    icon: 'heroicons_outline:clock'
  },
  'PRCD': {
    code: 'PRCD',
    name: 'Project Closed',
    color: 'gray',
    icon: 'heroicons_outline:lock-closed'
  },
  'ODDP': {
    code: 'ODDP',
    name: 'Order Dispatched',
    color: 'cyan',
    icon: 'heroicons_outline:truck'
  },
  'PYIN': {
    code: 'PYIN',
    name: 'Pay Invoice',
    color: 'blue',
    icon: 'heroicons_outline:currency-dollar'
  },
  'OPEN': {
    code: 'OPEN',
    name: 'Open',
    color: 'blue',
    icon: 'heroicons_outline:folder-open'
  },
  'NINT': {
    code: 'NINT',
    name: 'Not Interested',
    color: 'gray',
    icon: 'heroicons_outline:hand-raised'
  },
  'GNPO': {
    code: 'GNPO',
    name: 'PO Generated',
    color: 'green',
    icon: 'heroicons_outline:document-check'
  },
  'CLSD': {
    code: 'CLSD',
    name: 'Closed',
    color: 'gray',
    icon: 'heroicons_outline:x-circle'
  },
  'DLTD': {
    code: 'DLTD',
    name: 'Deleted',
    color: 'red',
    icon: 'heroicons_outline:trash'
  },
  'APQV': {
    code: 'APQV',
    name: 'Quote Approved',
    color: 'teal',
    icon: 'heroicons_outline:check-badge'
  },
  'RTQV': {
    code: 'RTQV',
    name: 'Quote Returned',
    color: 'rose',
    icon: 'heroicons_outline:arrow-uturn-left'
  },
  'PLCD': {
    code: 'PLCD',
    name: 'Order Placed',
    color: 'teal',
    icon: 'heroicons_outline:shopping-cart'
  },
  'ACPO': {
    code: 'ACPO',
    name: 'Accept PO',
    color: 'green',
    icon: 'heroicons_outline:check-circle'
  },
  'RJQT': {
    code: 'RJQT',
    name: 'Reject Quote',
    color: 'red',
    icon: 'heroicons_outline:x-mark'
  }
};

/**
 * Labels the API sends in `quotationStatus` that are not status codes.
 * They all ride on the same `statusCd` (QUOT), so without this map every one of
 * them would inherit the single QUOT colour and the list would look monochrome.
 * Keyed by the lower-cased label. Warm colours mean the seller has to act; cool
 * colours mean the request is with the buyer.
 */
export const QUOTATION_STATUS_CONFIG: Record<string, StatusInfo> = {
  'quote requested': {
    code: 'QUOT',
    name: 'Quote requested',
    color: 'amber',
    icon: 'heroicons_outline:pencil-square'
  },
  'negotiation received': {
    code: 'QUOT',
    name: 'Negotiation Received',
    color: 'orange',
    icon: 'heroicons_outline:arrow-down-left'
  },
  'quote submitted': {
    code: 'QUOT',
    name: 'Quote submitted',
    color: 'blue',
    icon: 'heroicons_outline:paper-airplane'
  },
  'negotiation submitted': {
    code: 'QUOT',
    name: 'Negotiation Submitted',
    color: 'indigo',
    icon: 'heroicons_outline:arrow-up-right'
  }
};

/**
 * Resolve a status value to its display configuration.
 * Accepts either a status code (`QUOT`) or an already human-readable label
 * (`Quotation Stage`, `Quote requested`), which is what the API's
 * `quotationStatus` field carries.
 * Returns null when the value matches neither, so callers can pick their own fallback.
 */
export function resolveStatusInfo(value: string | null | undefined): StatusInfo | null {
  const key = value?.trim();
  if (!key) return null;

  const byCode = STATUS_CONFIG[key] || STATUS_CONFIG[key.toUpperCase()];
  if (byCode) return byCode;

  const normalized = key.toLowerCase();
  const byQuotationLabel = QUOTATION_STATUS_CONFIG[normalized];
  if (byQuotationLabel) return byQuotationLabel;

  return Object.values(STATUS_CONFIG).find(info => info.name.toLowerCase() === normalized) || null;
}

/**
 * Interface for table display data (flattened and computed fields)
 */
export interface MaterialRequestTableData {
  id: number;
  prchOrdrId: number;
  prchOrdrSplitId: number | null;
  orderGroupId: number | null;
  sellerVendorId: number | null;
  statusCd: string;
  deliveryAddressId: number | null;
  lorryDriverName: string | null;
  lorryNumber: string | null;
  lorryDriverPhone: string | null;
  mrNumber: string;
  projectName: string;
  productName: string;
  category: string;
  quantity: number;
  unit: string;
  status: string;
  /** Raw quotationStatus from the API; null when the backend does not send one. */
  quotationStatus: string | null;
  /** Effective status label for display: quotationStatus when available, else the statusCd label. */
  statusName: string;
  /** Effective status colour for display, resolved from the same value as statusName. */
  statusColor: string;
  /** Effective status icon for display, resolved from the same value as statusName. */
  statusIcon: string;
  deliveryDate: Date;
  createdDate: Date;
  updatedDate: Date;
  submittedDate: Date | null;
  vendorName: string;
  customerName: string;
  location: string;
  isMr: boolean;
  mrReadYn: string;
  quoteSubmittedYn: string | null;
  assignedVendorsCount: number;
  price: number | null;
  images: string[];
  isNego: string;
  hasInstructions: boolean;
  priority?: 'high' | 'medium' | 'low';
  urgency?: 'urgent' | 'normal';
  noOfProducts?: number;
  products: MaterialRequestProduct[];
  accessRestrictions?: string | null;
  priorityLevel?: string | null;
  timeSlotLabel?: string | null;
  timeSlotCode?: string | null;
  receivingContactName?: string | null;
  receivingContactPhone?: string | null;
  receivingContactDesignation?: string | null;
  negotiationContactName?: string | null;
  negotiationContactPhone?: string | null;
  negotiationContactDesignation?: string | null;
  negotiationContactEmail?: string | null;
  negotiateContactPerson?:string | null;
}